/**
 * Client-side attachment rules for the failure drawer.
 *
 * These are deliberately *tighter* than the API's AttachmentConstraints /
 * AttachmentContentValidator (MedOne.TestHub.Api.Data.Contract.v1.Constraint), which
 * still accepts 10 files, 25 MB each, and documents/archives/video besides images. A
 * failure is reported with screenshots, so the drawer offers only that and says so at
 * the picker — instant feedback instead of a 400 after a long upload.
 *
 * Because these are a subset of what the server allows, anything that passes here passes
 * there. Loosening any of them past the API's own limits would not: check the server
 * constraint first.
 */

/** Largest single upload the drawer accepts (10 MB; the API itself allows 25 MB). */
export const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

/** Most files that may be attached to one test case result (the API allows 10). */
export const MAX_ATTACHMENTS_PER_RESULT = 5;

/** File name maximum length (matches the API's [FileName] column). */
export const MAX_ATTACHMENT_FILE_NAME_LENGTH = 260;

/**
 * Accepted extensions mapped to the content types allowed for each. The extension is
 * authoritative and the browser-supplied content type must agree with it — that pairing
 * is what stops a renamed executable arriving as "image/png".
 */
export const ALLOWED_ATTACHMENT_CONTENT_TYPES: Readonly<Record<string, readonly string[]>> = {
  '.png': ['image/png'],
  '.jpg': ['image/jpeg'],
  '.jpeg': ['image/jpeg'],
  '.gif': ['image/gif'],
};

export const ALLOWED_ATTACHMENT_EXTENSIONS = Object.keys(ALLOWED_ATTACHMENT_CONTENT_TYPES);

/**
 * Format names for the picker hint. Not derived from the extension list on purpose:
 * .jpg and .jpeg are one format to a tester and shouldn't be listed twice.
 */
export const ALLOWED_ATTACHMENT_FORMAT_LABELS = ['PNG', 'JPG', 'GIF'];

/** `accept` value for the file input. Extensions rather than MIME types, so a file the
    browser reports no type for still shows up in the picker and gets a real error from
    validateAttachment instead of being silently unselectable. */
export const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTENSIONS.join(',');

/** A File the browser gives no type for is posted as application/octet-stream, so that
    is what the server judges it by — validate against the same value. */
const FALLBACK_CONTENT_TYPE = 'application/octet-stream';

/** Lower-cased extension including the dot, or '' when the name has none. */
export function getFileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? '' : fileName.slice(dot).toLowerCase();
}

/** The content type the multipart part will actually carry. */
export function getEffectiveContentType(file: File): string {
  // Strip any "; charset=..." parameter, as the server does before comparing.
  const bare = (file.type ?? '').split(';')[0].trim();
  return bare || FALLBACK_CONTENT_TYPE;
}

/**
 * Returns null when the file is acceptable, otherwise why it was rejected — same order
 * of checks as the server's ValidateMetadata, against the narrower limits above.
 */
export function validateAttachment(file: File): string | null {
  if (!file.name.trim()) return 'That file has no name.';

  if (file.name.length > MAX_ATTACHMENT_FILE_NAME_LENGTH) {
    return `File name is longer than ${MAX_ATTACHMENT_FILE_NAME_LENGTH} characters.`;
  }

  if (file.size <= 0) return 'That file is empty.';

  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
    return `That file is ${formatFileSize(file.size)}; the limit is ${formatFileSize(
      MAX_ATTACHMENT_SIZE_BYTES,
    )}.`;
  }

  const extension = getFileExtension(file.name);
  if (!extension) return 'That file has no extension.';

  const allowedContentTypes = ALLOWED_ATTACHMENT_CONTENT_TYPES[extension];
  if (!allowedContentTypes) {
    return `${extension} files aren’t accepted. Allowed: ${ALLOWED_ATTACHMENT_EXTENSIONS.join(', ')}.`;
  }

  const contentType = getEffectiveContentType(file);
  if (!allowedContentTypes.some((allowed) => allowed.toLowerCase() === contentType.toLowerCase())) {
    return `That file says it is ${contentType}, which doesn’t match a ${extension} file.`;
  }

  return null;
}

/**
 * The warning shown for a file the drawer turns away. One wording for every rejection
 * (AC7): a rejected file never becomes a thumbnail, so there is nowhere to hang the
 * specific reason, and the tester's fix is the same either way — pick a supported image
 * within the size limit. validateAttachment still returns the precise reason for callers
 * that can show it.
 */
export function attachmentRejectionWarning(fileName: string): string {
  return `${fileName} is not a supported image, or exceeds ${formatFileSize(
    MAX_ATTACHMENT_SIZE_BYTES,
  )}.`;
}

/** Human-readable size for the picker list, e.g. "1.4 MB". */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  // One decimal below 10 MB, where the difference between 1.4 and 1.9 is worth seeing.
  return mb < 10 ? `${mb.toFixed(1)} MB` : `${Math.round(mb)} MB`;
}
